import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService, isSessionMismatchError, USER_ID_HEADER } from '../services/auth.service';

// Sign-in steps run before the tab has an identity, or are what gives it a new one.
const SIGN_IN_PATHS = [
  environment.LOGIN,
  environment.TWO_FACTOR_LOGIN_VERIFY,
  environment.FORGOT_PASSWORD,
  environment.OTP_VARIFICATION,
  environment.RESET_PASSWORD,
];

/**
 * Sends the user this tab belongs to as X-User-Id, so the backend refuses a stale tab instead
 * of letting it act as whoever signed in last on the shared cookie. Registered after
 * authTokenInterceptor: a mismatch invalidates the tab before that interceptor sees the 401,
 * so it does not try to refresh another user's session.
 */
export const userIdentityInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.API_URL)) {
    return next(req);
  }

  const path = req.url.slice(environment.API_URL.length).split('?')[0];
  if (SIGN_IN_PATHS.includes(path)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const userId = authService.getTabUserId();
  if (!userId) {
    return next(req);
  }

  return next(req.clone({ setHeaders: { [USER_ID_HEADER]: userId } })).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        (isSessionMismatchError(error) || !authService.ownsSharedSession())
      ) {
        authService.invalidateTabSession();
      }
      return throwError(() => error);
    })
  );
};
